import type { ReactNode } from "react";
import { cn } from "../lib/cn";

/** Keyboard hint, e.g. ⌘K. */
export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <kbd
      className={cn(
        "inline-flex h-4 items-center rounded-xs border border-border bg-surface px-1 font-mono text-xs text-fg-subtle",
        className,
      )}
    >
      {children}
    </kbd>
  );
}

export function Spinner({ className, size = 12 }: { className?: string; size?: number }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      style={{ width: size, height: size }}
      className={cn(
        "inline-block animate-spin rounded-full border-2 border-accent border-r-transparent",
        className,
      )}
    />
  );
}

export interface StatTileProps {
  label: string;
  value: ReactNode;
  /** Muted denominator or unit shown after the value, e.g. "/322". */
  suffix?: ReactNode;
  sub?: ReactNode;
  delta?: { text: string; tone: "ok" | "warn" | "err" | "neutral" };
  tone?: "default" | "err" | "warn";
  onClick?: () => void;
  children?: ReactNode;
  className?: string;
  /** Right end of the header row: a link ("All tests"), a mono id ("#4812") or a window ("14 days"). */
  action?: ReactNode;
  /** 0..1 fills a 6px meter under the value (trial minutes, seats, credits). */
  progress?: { value: number; tone?: "ok" | "warn" | "err" | "accent" };
}

const deltaTone = {
  ok: "text-ok-fg",
  warn: "text-warn-fg",
  err: "text-err-fg",
  neutral: "text-fg-subtle",
};

/**
 * KPI tile. Clickable tiles act as filters and get a hover state. The header `action` and the
 * `children` (a run strip, a legend) sit outside the clickable element, so they can be buttons
 * themselves: a button cannot contain a button.
 */
export function StatTile({
  label,
  value,
  suffix,
  sub,
  delta,
  tone = "default",
  onClick,
  children,
  className,
  action,
  progress,
}: StatTileProps) {
  const Comp = onClick ? "button" : "div";
  return (
    <div
      className={cn(
        "relative flex flex-col rounded-md border bg-surface",
        tone === "err"
          ? "border-err-border"
          : tone === "warn"
            ? "border-warn-border"
            : "border-border",
        className,
      )}
    >
      <Comp
        type={onClick ? "button" : undefined}
        onClick={onClick}
        className={cn(
          "flex flex-col gap-1 rounded-md px-4 py-3 text-left",
          onClick &&
            "transition-colors hover:bg-hover focus-visible:outline-2 focus-visible:outline-ring",
        )}
      >
        <span
          className={cn(
            "flex items-center gap-2 text-sm font-semibold text-fg-muted",
            action && "pr-20",
          )}
        >
          {label}
        </span>
        <span className="flex items-baseline gap-1.5">
          <span
            className={cn(
              "text-2xl leading-7 font-semibold tracking-tight",
              tone === "err" && "text-err-fg",
            )}
          >
            {value}
          </span>
          {suffix ? <span className="text-fg-subtle">{suffix}</span> : null}
          {delta ? (
            <span className={cn("text-sm font-medium", deltaTone[delta.tone])}>{delta.text}</span>
          ) : null}
        </span>
        {progress ? <Meter value={progress.value} tone={progress.tone} /> : null}
        {sub ? <span className="text-sm text-fg-subtle">{sub}</span> : null}
      </Comp>
      {action ? (
        <span className="absolute top-3 right-4 flex items-center text-sm text-fg-muted">
          {action}
        </span>
      ) : null}
      {children ? <div className="flex flex-col gap-1 px-4 pb-3">{children}</div> : null}
    </div>
  );
}

/** 6px horizontal meter; `value` is 0..1. The bar is decoration: the number beside it carries the value. */
export function Meter({
  value,
  tone = "accent",
  className,
}: {
  value: number;
  tone?: "ok" | "warn" | "err" | "accent";
  className?: string;
}) {
  const fill = { ok: "bg-ok", warn: "bg-warn", err: "bg-err", accent: "bg-accent" }[tone];
  return (
    <span
      aria-hidden
      className={cn("block h-1.5 w-full overflow-hidden rounded-pill bg-surface-inset", className)}
    >
      <span
        className={cn("block h-full rounded-pill", fill)}
        style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%` }}
      />
    </span>
  );
}

export interface EmptyStateProps {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({ title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn("flex flex-col items-center gap-1.5 px-6 py-12 text-center", className)}>
      <span className="font-medium text-fg">{title}</span>
      {description ? <span className="max-w-md text-fg-muted">{description}</span> : null}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}

/**
 * Card with the canvas anatomy: bordered surface, optional header row (title · meta · actions)
 * and optional tinted footer strip (the board closes many cards with one: a status line, a
 * "Showing n of m" count, a pair of quiet actions).
 */
export function Card({
  title,
  meta,
  actions,
  footer,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  meta?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section
      className={cn("overflow-hidden rounded-md border border-border bg-surface", className)}
    >
      {title ? (
        <header className="flex items-center gap-2 border-b border-border-subtle px-4 py-2.5">
          <h3 className="text-md font-semibold text-fg">{title}</h3>
          {meta ? <span className="text-fg-subtle">{meta}</span> : null}
          <span className="flex-1" />
          {actions}
        </header>
      ) : null}
      <div className={bodyClassName}>{children}</div>
      {footer ? (
        <footer className="flex items-center gap-3 border-t border-border-subtle bg-surface-subtle px-4 py-2 text-sm text-fg-muted">
          {footer}
        </footer>
      ) : null}
    </section>
  );
}
